<template>
  <Dialog
    v-model:visible="showDialog"
    :modal="true"
    :closable="!isMobile"
    :draggable="false"
    :dismissableMask="false"
    class="custom-dialog fixNotchPaddingTop"
    :style="isMobile ? { width: '100%', height: '100%' } : { width: '1150px', maxHeight: '90%' }"
    :contentStyle="{ overflowY: 'auto' }"
    style="z-index: 20003"
  >
    <!-- Header personalizado para móvil -->
    <template v-if="isMobile" #header>
      <div class="mobile-header">
        <Button 
          icon="pi pi-arrow-left" 
          text 
          rounded
          severity="secondary"
          @click="close()" 
        />
        <span class="mobile-title">
          {{ !data.id ? t('wkt.client.expensesheets.edit-dialog.new_title') : t('wkt.client.expensesheets.edit-dialog.edit_title') }}
        </span>
      </div>
    </template>

    <!-- Header normal para desktop -->
    <template v-else #header>
      <span class="dialog-title">
        {{ !data.id ? t('wkt.client.expensesheets.edit-dialog.new_title') : t('wkt.client.expensesheets.edit-dialog.edit_title') }}
      </span>
    </template>

    <ExpenseSheetSkeleton v-if="isLoading" />

    <!-- Formulario -->
    <Form v-else ref="form" :resolver @submit="onFormSubmit" @wheel.stop>
      <!-- Campo oculto: id -->
      <FormField name="id" :initialValue="data.id">
        <InputText type="hidden" />
      </FormField>

      <!-- Campo oculto: companyId -->
      <FormField name="companyId" :initialValue="data.companyId">
        <InputText type="hidden" />
      </FormField>

      <!-- Campo: name -->
      <FormField v-slot="$field" name="name" :initialValue="data.name" class="field">
        <label for="name" class="field-label">
          {{ t('wkt.client.expensesheets.edit-dialog.name') }}
        </label>
        <InputText
          id="name"
          v-model="data.name"
          :placeholder="t('wkt.client.expensesheets.edit-dialog.name_placeholder')"
          :invalid="$field?.invalid"
          maxlength="255"
          class="w-full"
        />
        <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
          {{ $field.error?.message }}
        </Message>
      </FormField>

      <!-- Campo: project -->
      <FormField v-slot="$field" name="projectId" :initialValue="data.projectId" class="field">
        <label for="project" class="field-label">
          {{ t('wkt.client.expensesheets.edit-dialog.project') }}
        </label>
        <Dropdown
          id="project"
          v-model="data.projectId"
          :options="projects"
          optionLabel="shortName"
          optionValue="id"
          :placeholder="t('wkt.client.expensesheets.edit-dialog.project_placeholder')"
          class="w-full"
          :showClear="true"
        />
        <Message v-if="$field?.invalid" severity="error" size="small" variant="simple">
          {{ $field.error?.message }}
        </Message>
      </FormField>
    </Form>

    <!-- Footer con botones -->
    <template #footer>
      <div class="dialog-footer justify-content-between w-full" :class="{ 'mobile-footer': isMobile }">
          <Button
            v-if="data.id"
            class="dialog-footer-button uppercase"
            :label="t('wkt.client.common.dialog-buttons.delete')"
            severity="danger"
            text
            @click="confirmDelete()"
          />
          <div class="spacer"></div>
          <div>
            <Button
              class="footer-primary-button"
              :label="t('wkt.client.common.dialog-buttons.save')"
              color="#185EA9"
              variant="text"
              @click="form.onSubmit()"
            />
            <Button
              class="footer-button-cancel"
              :label="t('wkt.client.common.dialog-buttons.cancel')"
              severity="secondary"
              text
              @click="close()"
            />
          </div>
        
      </div>
    </template>
  </Dialog>

  <!-- Diálogo de confirmación de eliminación -->
  <ConfirmDialog
    ref="dlgConfirm"
    :title="t('wkt.client.expensesheets.edit-window.confirm-delete-dialog.title')"
    :message="t('wkt.client.expensesheets.edit-window.confirm-delete-dialog.text')"
    :titleOk="t('wkt.client.common.dialog-buttons.delete')"
    :titleCancel="t('wkt.client.common.dialog-buttons.cancel')"
    @confirmed="deleteItem()"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { yupResolver } from '@primevue/forms/resolvers/yup'
import * as yup from 'yup'
import ConfirmDialog from '@wkt/components/common/ConfirmDialog.vue'
import expenseSheetsApi from '@wkt/api/expense-sheets'
import ExpenseSheetSkeleton from '@wkt/components/modules/skeletons/expensesheets/ExpenseSheetSkeleton.vue'
import { gwtApi } from '@wkt/api/gwt/gwtAPI'
import type { ExpenseSheet } from '@wkt/api/expense-sheets'
import { useAlert } from '@wkt/composables/useAlert'

// Composables
const { t } = useI18n()
const { showAlert } = useAlert()

// Emits
const emit = defineEmits<{
  'item-updated': [item: ExpenseSheet]
  'item-deleted': []
}>()

// State
const showDialog = ref(false)
const isLoading = ref(false)
const isMobile = ref(false)
const projects = ref<any[]>([])
const data = ref<Partial<ExpenseSheet>>({})
const msgCbUpdated = ref<((item: ExpenseSheet) => void) | null>(null)
const msgCbDeleted = ref<(() => void) | null>(null)
const dlgConfirm = ref<InstanceType<typeof ConfirmDialog> | null>(null)
const form = ref()

// Esquema de validación con yup
const resolver = yupResolver(
  yup.object({
    id: yup.number().nullable(),
    companyId: yup.number().nullable(),
    name: yup.string().required(t('wkt.client.common.validation-utils.required_field')),
    projectId: yup.number().nullable()
  })
)

// Methods
function checkMobile() {
  isMobile.value = window.innerWidth < 768
}

function open(id?: number | null, cbUpdated?: (item: ExpenseSheet) => void, cbDeleted?: () => void) {
  msgCbUpdated.value = cbUpdated || null
  msgCbDeleted.value = cbDeleted || null

  showDialog.value = true

  // Obtener proyectos del empleado desde la sesión
  const sessionInfo = gwtApi.getSessionInformation()
  const employeeProjects = sessionInfo?.employeeProjects || []
  
  projects.value = employeeProjects.filter((p: any) => p.enabled)

  if (!id) {
    // Modo crear: inicializar datos vacíos
    data.value = {
      companyId: sessionInfo?.company?.id
    }
    return
  }

  // Modo editar: cargar datos existentes
  getExpenseSheetById(id)
}

function close() {
  showDialog.value = false
}

function getExpenseSheetById(id: number) {
  isLoading.value = true

  expenseSheetsApi.getExpenseSheetById(
    id,
    (response) => {
      isLoading.value = false
      data.value = response
    },
    (err: any) => {
      isLoading.value = false
      console.error('Error al cargar hoja de gastos:', err)
      showAlert({ 
        text: err.message || 'Error al cargar hoja de gastos', 
        color: 'error'
      })
    }
  )
}

function onFormSubmit(event: any) {
  if (!event.valid) {
    return
  }
  data.value = {
    ...data.value,
    ...event.values 
  }

  save()
}

function save() {
  isLoading.value = true

  expenseSheetsApi.storeExpenseSheet(
    data.value as ExpenseSheet,
    (response) => {
      isLoading.value = false

      if (!response) {
        showAlert({ 
          text: t('wkt.client.expensesheets.edit-dialog.save_error'), 
          color: 'error'
        })
        return
      }

      showDialog.value = false

      showAlert({ 
        text: t('wkt.client.expensesheets.edit-dialog.save_ok'), 
        color: 'success'
      })

      emit('item-updated', response)

      if (msgCbUpdated.value) {
        msgCbUpdated.value(response)
      }
    },
    (err: any) => {
      isLoading.value = false
      console.error('Error al guardar hoja de gastos:', err)
      showAlert({ 
        text: err.message || 'Error al guardar hoja de gastos', 
        color: 'error'
      })
    }
  )
}

function confirmDelete() {
  if (dlgConfirm.value) {
    dlgConfirm.value.confirm(data.value)
  }
}

function deleteItem() {
  if (!data.value.id) return

  isLoading.value = true

  expenseSheetsApi.deleteExpenseSheetById(
    data.value.id,
    (res) => {
      isLoading.value = false
      showDialog.value = false

      if (res) {
        showAlert({ 
          text: t('wkt.client.expensesheets.edit-dialog.delete_ok'), 
          color: 'success'
        })
      } else {
        showAlert({ 
          text: t('wkt.client.expensesheets.edit-dialog.delete_error'), 
          color: 'error'
        })
      }

      emit('item-deleted')

      if (msgCbDeleted.value) {
        msgCbDeleted.value()
      }
    },
    (err: any) => {
      isLoading.value = false
      console.error('Error al eliminar hoja de gastos:', err)
      showAlert({ 
        text: err.message || 'Error al eliminar hoja de gastos', 
        color: 'error'
      })
    }
  )
}

// Lifecycle
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

// Exponer métodos para uso externo
defineExpose({
  open,
  close
})
</script>

<style scoped>
.mobile-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.mobile-title {
  font-size: 1.5rem;
  font-weight: 500;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 500;
}

.dialog-footer {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.mobile-footer {
  padding-bottom: env(safe-area-inset-bottom);
}

.spacer {
  flex: 1;
}

.w-full {
  width: 100%;
}
</style>